import React from 'react';
import { useSelector } from 'react-redux';
import { MenuItem } from '@mui/material';
import { Order, DataType, getOrderLabel, AwardingRuleParameterType } from '../../../../Enums';
import StyledSelect from '../StyledSelect';
import InputTitle from '../InputTitle';
import './Step2-SortingRuleSection.scss';

const typePrefix = ["Long", "Date", "Double", "Time"];

/**
 * @param {{
 *  currentAwardingRule: ApiAdmin.IBodyAwardingRules,
 *  onChange: Function,
 *  isDisabled: boolean,
 *  fieldValidationErrors: {[Key: string] : boolean},
 * }} props
 */
function CustomFieldParameter(props) {
    const {
        currentAwardingRule,
        onChange = () => { },
        isDisabled,
        fieldValidationErrors,
    } = props;

    /** @type {AdminAppData.IAppData} */
    const adminAppData = useSelector((/** @type {EmploymentBillboard.AdminStore} */ state) => state.adminAppData);
    /** @type {RuleSet.IRuleSetDetails} */
    const adminRuleSet = useSelector((/** @type {EmploymentBillboard.AdminStore} */ state) => state.adminRuleSet);
    const { awardingRules } = adminRuleSet;
    const { customDataFields, customDateFormat: customDateFormatList } = adminAppData;
    const { customDataFieldId, dataType, customDateFormat } = currentAwardingRule.customDataFieldInfos;

    /**
    * @typedef {{value: string, label: string, disabled: boolean}} SelectDataviewFieldOption
    * @type {SelectDataviewFieldOption[]}
    */
    const customFields = customDataFields.map((x) => ({
        value: x.id,
        label: x.name,
        disabled: awardingRules.findIndex((parameter) => parameter.type === AwardingRuleParameterType.CustomDataType && parameter.customDataFieldInfos.customDataFieldId === x.id) !== -1,
    })) || [];

    /**
     * @typedef {{value: Order, label: string}} SelectOption
     * @type {SelectOption[]}
     */
    const orderOptions = Object.values(Order).map((x) => ({
        value: x, label: getOrderLabel(x),
    }));

    /**
     * @typedef {{value: DataType | number, label: string}} DataTypeSelectOption
     * @type {DataTypeSelectOption[]}
     */
    const dataTypeOptions = [{ value: DataType.Numeric, type: DataType.Numeric, label: 'Number' }];
    for (const format of customDateFormatList) {
        dataTypeOptions.push({ value: format.id, type: format.dataType, label: `${typePrefix[format.dataType]} (${format.name})` });
    }

    const selectedCustomField = customFields.find((x) => x.value === customDataFieldId);
    const selectedCustomDateFormat = dataTypeOptions.find((x) => x.value === dataType || x.value === customDateFormat);
    const selectedOrder = orderOptions.find((x) => x.value === currentAwardingRule.order);

    return (
        <div className="srs--dataviewCustomHyperfind-container">
            <div className="rse--selection-field-container srs-data-container">
                <InputTitle>Data</InputTitle>
                <StyledSelect
                    labelId="select-custom-field-label"
                    id="select-custom-field-type"
                    disabled={isDisabled}
                    renderValue={(/** @type {string} */
                        _value,
                    ) => selectedCustomField?.label}
                    value={selectedCustomField?.value ?? ''}
                    onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                        event,
                    ) => onChange('customDataFieldId', event.target.value)}
                >
                    {customFields.map((x) => (
                        <MenuItem
                            value={x.value}
                            key={`SelectItem_${x.label}`}
                            disabled={x.disabled}
                        >
                            {x.label}
                        </MenuItem>
                    ))}
                </StyledSelect>
                {
                    fieldValidationErrors.customDataFieldId ? <span style={{ color: 'red' }}>Please select a custom field</span> : null
                }
            </div>
            <div className="rse--selection-field-container srs-dataAndOrderType-container">
                <InputTitle>Data type</InputTitle>
                <StyledSelect
                    labelId="select-data-type-label"
                    id="select-data-type-type"
                    disabled={isDisabled}
                    renderValue={(/** @type {string} */
                        _value,
                    ) => selectedCustomDateFormat?.label}
                    value={selectedCustomDateFormat?.value ?? ''}
                    onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                        event,
                    ) => {
                        var selectedOption = dataTypeOptions.find(x => x.value === event.target.value);
                        onChange('dataType', selectedOption);
                    }}
                >
                    {dataTypeOptions.map((x) => (
                        <MenuItem
                            value={x.value}
                            key={`SelectItem_${x.label}`}
                        >
                            {x.label}
                        </MenuItem>
                    ))}
                </StyledSelect>
                {
                    fieldValidationErrors.dataType || fieldValidationErrors.customDateFormat ? <span style={{ color: 'red' }}>Please select a data type</span> : null
                }
            </div>
            <div className="rse--selection-field-container srs-dataAndOrderType-container">
                <InputTitle>Order type</InputTitle>
                <StyledSelect
                    labelId="select-sort-order-type-label"
                    id="select-sort-order-type-type"
                    disabled={isDisabled}
                    renderValue={(/** @type {string} */
                        _value,
                    ) => selectedOrder?.label}
                    value={selectedOrder?.value ?? ''}
                    onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                        event,
                    ) => onChange('order', event.target.value)}
                >
                    {orderOptions.map((x) => (
                        <MenuItem
                            value={x.value}
                            key={`SelectItem_${x.label}`}
                        >
                            {x.label}
                        </MenuItem>
                    ))}
                </StyledSelect>
                {
                    fieldValidationErrors.order ? <span style={{ color: 'red' }}>Please select a sorting order</span> : null
                }
            </div>
        </div>
    );
}

export default CustomFieldParameter;